import { baseUrl } from "../../../baseUrl";
import { ExpandMore } from "@mui/icons-material";
import { formatPrice } from "../../../utils/formatPrice";
import { fetchWithAuthHandling } from "../../../authHandling";
import { type ChangeEvent, useEffect, useState } from "react";
import type { CurrencyAmount } from "../../../interfaces/buyerProfileInterface";
import {
  convertToFormattedAmount,
  convertToNumberAndMultiplyBy100,
} from "../../../utils/convertAmount";
import {
  Box,
  Card,
  Paper,
  Button,
  Divider,
  TextField,
  Accordion,
  Typography,
  CardContent,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

interface AffordabilityPrefill {
  annualIncome: CurrencyAmount | null;
  monthlyCondoFees: CurrencyAmount | null;
  downPaymentAmount: CurrencyAmount | null;
  monthlyDebtPayments: CurrencyAmount | null;
  annualPropertyTaxes: CurrencyAmount | null;
  monthlyHeatingCosts: CurrencyAmount | null;
}

interface ToolsSectionsProps {
  prefillData: AffordabilityPrefill;
  buyerId: string;
}

export const ToolsSections = ({ prefillData, buyerId }: ToolsSectionsProps) => {
  const [annualIncome, setAnnualIncome] = useState<string>("");
  const [downPaymentAmount, setDownPaymentAmount] = useState<string>("");
  const [monthlyDebtPayments, setMonthlyDebtPayments] = useState<string>("");
  const [annualPropertyTaxes, setAnnualPropertyTaxes] = useState<string>("");
  const [monthlyHeatingCosts, setMonthlyHeatingCosts] = useState<string>("");
  const [monthlyCondoFees, setMonthlyCondoFees] = useState<string>("");
  const [dataInitialized, setDataInitialized] = useState(false);
  const [affordability, setAffordability] = useState({
    maxAffordability: 0,
    totalSavingsNeeded: 0,
    zownContribution: 0,
    newTotalSavings: 0,
    monthlyPayments: 0,
    totalMonthlySavings: 0,
    totalMortgageSavings: 0,
    totalSavingsNeededPercentage: 0
  });

  useEffect(() => {
    const initializeData = () => {
      setAnnualIncome(
        prefillData.annualIncome
          ? formatPrice(
            convertToFormattedAmount(prefillData.annualIncome.amount)
          )
          : ""
      );
      setDownPaymentAmount(
        prefillData.downPaymentAmount
          ? formatPrice(
            convertToFormattedAmount(prefillData.downPaymentAmount.amount)
          )
          : ""
      );
      setMonthlyDebtPayments(
        prefillData.monthlyDebtPayments
          ? formatPrice(
            convertToFormattedAmount(prefillData.monthlyDebtPayments.amount)
          )
          : ""
      );
      setAnnualPropertyTaxes(
        prefillData.annualPropertyTaxes
          ? formatPrice(
            convertToFormattedAmount(prefillData.annualPropertyTaxes.amount)
          )
          : ""
      );
      setMonthlyHeatingCosts(
        prefillData.monthlyHeatingCosts
          ? formatPrice(
            convertToFormattedAmount(prefillData.monthlyHeatingCosts.amount)
          )
          : ""
      );
      setMonthlyCondoFees(
        prefillData.monthlyCondoFees
          ? formatPrice(
            convertToFormattedAmount(prefillData.monthlyCondoFees.amount)
          )
          : ""
      );
      setDataInitialized(true);
    };

    initializeData();
  }, [prefillData]);

  useEffect(() => {
    if (dataInitialized) {
      handleCalculate();
    }
  }, [dataInitialized]);

  const handleCalculate = async () => {
    const payload = {
      annualIncome: annualIncome
        ? {
          amount: convertToNumberAndMultiplyBy100(annualIncome),
          currency: "CAD"
        }
        : null,
      downPaymentAmount: downPaymentAmount
        ? {
          amount: convertToNumberAndMultiplyBy100(downPaymentAmount),
          currency: "CAD"
        }
        : null,
      monthlyDebtPayments: monthlyDebtPayments
        ? {
          amount: convertToNumberAndMultiplyBy100(monthlyDebtPayments),
          currency: "CAD"
        }
        : null,
      annualPropertyTaxes: annualPropertyTaxes
        ? {
          amount: convertToNumberAndMultiplyBy100(annualPropertyTaxes),
          currency: "CAD"
        }
        : null,
      monthlyHeatingCosts: monthlyHeatingCosts
        ? {
          amount: convertToNumberAndMultiplyBy100(monthlyHeatingCosts),
          currency: "CAD"
        }
        : null,
      monthlyCondoFees: monthlyCondoFees
        ? {
          amount: convertToNumberAndMultiplyBy100(monthlyCondoFees),
          currency: "CAD"
        }
        : null
    };

    try {
      const response = await fetchWithAuthHandling(
        `${baseUrl}/buyers/${buyerId}/tools/1`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          credentials: "include",
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch affordability data");
      }

      const data = await response.json();

      setAffordability({
        maxAffordability: data.maximumAffordability?.amount
          ? convertToFormattedAmount(data.maximumAffordability?.amount)
          : 0,
        totalSavingsNeeded: data.totalSavingsNeeded?.amount
          ? convertToFormattedAmount(data.totalSavingsNeeded?.amount)
          : 0,
        zownContribution: data.zownContribution?.amount
          ? convertToFormattedAmount(data.zownContribution?.amount)
          : 0,
        newTotalSavings: data.newTotalSavingsNeeded?.amount
          ? convertToFormattedAmount(data.newTotalSavingsNeeded?.amount)
          : 0,
        monthlyPayments: data.monthlyPayments?.amount
          ? convertToFormattedAmount(data.monthlyPayments?.amount)
          : 0,
        totalMonthlySavings: data.totalMonthlySavings?.amount
          ? convertToFormattedAmount(data.totalMonthlySavings?.amount)
          : 0,
        totalMortgageSavings: data.totalMortgageSavings?.amount
          ? convertToFormattedAmount(data.totalMortgageSavings?.amount)
          : 0,
        totalSavingsNeededPercentage: data.totalSavingsNeededPercentage
          ? convertToFormattedAmount(data.totalSavingsNeededPercentage)
          : 0
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setValue: (value: string) => void
  ) => {
    const inputValue = event.target.value.replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(inputValue)) return;
    const numberValue = Number.parseFloat(inputValue);
    if (!Number.isNaN(numberValue)) {
      setValue(formatPrice(numberValue));
    } else {
      setValue("");
    }
  };

  return (
    <Paper elevation={4} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight="bold">
            Affordability Calculator
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              gap: 3,
              width: "100%",
              display: "flex",
              alignItems: "center",
              flexDirection: {
                xs: "column",
                sm: "row"
              }
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography variant="subtitle1" gutterBottom>
                Enter your details to calculate affordability:
              </Typography>
              <Box
                component="form"
                noValidate
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Annual Income ($)"
                  value={annualIncome}
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleChange(e, setAnnualIncome)}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Down Payment ($)"
                  value={downPaymentAmount}
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleChange(e, setDownPaymentAmount)}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Monthly Debt ($)"
                  value={monthlyDebtPayments}
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleChange(e, setMonthlyDebtPayments)}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Property Tax ($)"
                  value={annualPropertyTaxes}
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleChange(e, setAnnualPropertyTaxes)}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Heating Cost ($)"
                  value={monthlyHeatingCosts}
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleChange(e, setMonthlyHeatingCosts)}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Condo Fees ($)"
                  value={monthlyCondoFees}
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleChange(e, setMonthlyCondoFees)}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleCalculate();
                  }}
                  sx={{
                    textTransform: "none",
                    mt: 2,
                    fontWeight: "bold"
                  }}
                >
                  Calculate
                </Button>
              </Box>
            </Box>
            <Box sx={{ width: "100%" }}>
              <Card elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Affordability Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <Typography variant="subtitle1" color="primary">
                        Maximum Affordability:
                      </Typography>
                      <Typography
                        variant="h5"
                        color="secondary"
                        fontWeight="bold"
                      >
                        ${formatPrice(affordability.maxAffordability)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle1">
                          Total Savings Needed{" "}
                          {affordability.totalSavingsNeededPercentage &&
                            `(${affordability.totalSavingsNeededPercentage}%)`}
                          :
                        </Typography>
                        <Typography variant="body1">
                          ${formatPrice(affordability.totalSavingsNeeded)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle1">
                          Zown's Contribution:
                        </Typography>
                        <Typography variant="body1">
                          ${affordability.zownContribution.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle1">
                          New Total Savings Needed:
                        </Typography>
                        <Typography variant="body1">
                          ${formatPrice(affordability.newTotalSavings)}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle1">
                          Monthly Payments:
                        </Typography>
                        <Typography variant="body1">
                          ${formatPrice(affordability.monthlyPayments)}/month
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle1">
                          Total Monthly Savings:
                        </Typography>
                        <Typography variant="body1">
                          ${formatPrice(affordability.totalMonthlySavings)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle1">
                          Total Mortgage Savings (30-Year):
                        </Typography>
                        <Typography variant="body1">
                          ${formatPrice(affordability.totalMortgageSavings)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};
